import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, TrendingUp, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const NAV_LINKS = [
  { to: "/loans/personal", label: "Personal Loans" },
  { to: "/loans/debt-consolidation", label: "Debt Consolidation" },
  { to: "/loans/home-improvement", label: "Home Improvement" },
  { to: "/loans/auto", label: "Auto Loans" },
  { to: "/loans/business", label: "Business" },
  { to: "/#how-it-works", label: "How it Works" },
  { to: "/#faq", label: "FAQ" },
  { to: "/#contact", label: "Contact" },
];

export default function Nav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "ef-glass border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="nav-logo">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#1B63F0] to-[#071A35] flex items-center justify-center shadow-[0_6px_20px_-6px_rgba(27,99,240,0.7)]">
            <TrendingUp className="h-4 w-4 text-white" strokeWidth={2.6} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-lg tracking-tight text-white">Elevate</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#D4AF37] -mt-0.5">Financial</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-3 py-2 text-sm text-white/75 hover:text-white transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-2.5">
          <Link to="/apply">
            <Button data-testid="nav-apply-now" className="ef-btn-primary rounded-full px-5 h-9 text-sm font-medium">
              Apply Now <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          </Link>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          data-testid="mobile-nav-toggle"
          className="lg:hidden inline-flex h-9 w-9 items-center justify-center rounded-lg text-white hover:bg-white/10"
          aria-label="Menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden ef-glass border-t border-white/10">
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} className="py-2 text-sm text-white/80" data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                {l.label}
              </Link>
            ))}
            <div className="h-px bg-white/10 my-2" />
            <Link to="/apply" className="py-2 text-sm text-[#D4AF37]" data-testid="mobile-apply-now">Apply Now</Link>
          </div>
        </div>
      )}
    </header>
  );
}
